if (!customElements.get('card-product-variant-picker')) {
  customElements.define(
    'card-product-variant-picker',
    class CardProductVariantPicker extends HTMLElement {
      onVariantChangeUnsubscriber = undefined;
      
      constructor() {
        super();

        this.card = this.closest('.card');
        this.variantPicker = this.querySelector('variant-selects');
        this.optionsNames = this.querySelectorAll('[data-selected-value]');
        this.options = [];
        this.currentVariant = this.variantData;
      }

      connectedCallback() {
        if (!this.variantPicker) return;
        
        this.updateOptions();
        this.updateVariantStatuses();

        this.onVariantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.optionValueSelectionChange,
          this.handleOptionValueChange.bind(this)
        );
      }

      disconnectedCallback() {
        this.onVariantChangeUnsubscriber();
      }

      get variantsData() {
        return JSON.parse(this.querySelector('[data-variants]').textContent);
      }

      get variantData() {
        return JSON.parse(this.card?.querySelector('[data-current-variant]')?.textContent);
      }

      handleOptionValueChange({ data: { event, target, selectedOptionValues, context } }) {
        if (!this.contains(event.target) || context == 'main-product') return;
    
        this.updateOptions();
        this.updateOptionsNames();
        this.updateCurrentVariant(event);
        this.updateVariantStatuses();
        this.updateProductInfo();
        this.dispatchVariantChangeEvent();
      }

      updateOptions() {
        this.options = [];
        this.options.push(
          ...Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'), (element) => {
            return element.value;
          })
        );
      }

      updateOptionsNames() {
        this.optionsNames.forEach((optionName, index) => {
          optionName.innerText = this.options[index];
        });
      }

      updateCurrentVariant(event) {
        this.oldVariant = this.currentVariant;
        
        if (this.dataset.pickerType == 'dropdown_unique') {
          this.currentVariant = this.variantsData.find((variant) => variant.id == this.options[0]);
        } else {
          this.currentVariant = this.variantsData.find((variant) => {
            return !variant.options
              .map((option, index) => {
                return this.options[index] === option;
              })
              .includes(false);
          });
        }
        
        if (this.card.querySelector('[data-current-variant]').textContent) {
          this.card.querySelector('[data-current-variant]').textContent = JSON.stringify(this.currentVariant);
        }
      }

      updateVariantStatuses() {
        const selectedOptions = this.querySelectorAll(':checked');
        const inputWrappers = [...this.querySelectorAll('.product-form__input')];
        let selectedOptionOneVariants = null;
        let selectedOptionTwoVariants = null;
        let selectedOptionThreeVariants = null;
        if (selectedOptions[0]) {
          selectedOptionOneVariants = this.variantsData.filter((variant) => selectedOptions[0].value === variant.option1);
        }
        if (selectedOptions[1]) {
          selectedOptionTwoVariants = this.variantsData.filter((variant) => selectedOptions[1].value === variant.option2);
        }
        if (selectedOptions[2]) {
          selectedOptionThreeVariants = this.variantsData.filter((variant) => selectedOptions[2].value === variant.option3);
        }
        inputWrappers.forEach((option, index) => {
          if (selectedOptions.length <= 1) return;
          if (index === 0) {
            const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
            const previousOptionSelected = inputWrappers[index + 1].querySelector(':checked').value;
            const availableOptionInputsValue = selectedOptionTwoVariants.filter((variant) => variant.available && variant[`option${index + 2}`] === previousOptionSelected).map((variantOption) => variantOption[`option${index + 1}`]);
            this.setInputAvailability(optionInputs, availableOptionInputsValue);
          } else {
            const optionInputs = [...option.querySelectorAll('input[type="radio"], option')];
            const previousOptionSelected = inputWrappers[index - 1].querySelector(':checked').value;
            const availableOptionInputsValue = selectedOptionOneVariants.filter((variant) => variant.available && variant[`option${index}`] === previousOptionSelected).map((variantOption) => variantOption[`option${index + 1}`]);
            this.setInputAvailability(optionInputs, availableOptionInputsValue);
          }
        });
      }

      setInputAvailability(elementList, availableValuesList) {
        elementList.forEach((element) => {
          const value = element.getAttribute('value');
          const availableElement = availableValuesList.includes(value);

          availableElement ? element.classList.remove('disabled') : element.classList.add('disabled');
        });
      }

      updateProductInfo() {
        this.updateMedia();
        this.updatePrices();
        this.updateCurrentVariantId();
        this.updateButtonStatuses();
      }

      updateMedia() {
        const media = this.card?.querySelector('.card__media .media');
        const image = media?.querySelector('img');
        
        if (!image || !this.currentVariant['featured_media']) return;

        const widthsAttribute = image.widths;
        const sizesAttribute = image.sizes;
        const classAttribute = image.className;
        const newImage = this.createMediaImg(this.currentVariant['featured_media'], this.getMediaWidths(image), { class: classAttribute, sizes: sizesAttribute, widths: widthsAttribute });

        media.replaceChildren(newImage);
      }

      getMediaWidths(image) {
        return image.getAttribute('widths')
          .split(',')
          .map((width) => parseInt(width));
      }

      createMediaImg(media, widths = [], properties = {}) {
        const image = new Image(media['preview_image']['width'], media['preview_image']['height']);
        let featuredMediaSrc = media['preview_image']['src'];
        if (!featuredMediaSrc.startsWith('http')) featuredMediaSrc = 'https://' + featuredMediaSrc;
        const featuredMediaUrl = new URL(featuredMediaSrc);

        for (const propertyKey in properties) {
          image.setAttribute(propertyKey, properties[propertyKey]);
        }

        image.alt = media['alt'];
        image.src = featuredMediaUrl.href;
        image.srcset = this.generateSrcset(media['preview_image'], widths);

        return image;
      }

      generateSrcset(imageObject, widths = []) {
        let imageSrc = imageObject['src'];
        if (!imageSrc.startsWith('http')) imageSrc = 'https://' + imageSrc;
        const imageUrl = new URL(imageSrc);

        return widths
          .filter((width) => width <= imageObject['width'])
          .map((width) => {
            imageUrl.searchParams.set('width', width.toString());
            return `${imageUrl.href} ${width}w`;
          })
          .join(', ');
      }

      updatePrices() {
        const price = this.card?.querySelector('.price');
        const priceRegular = price?.querySelector('.price__regular');
        const priceSale = price?.querySelector('.price__sale');
        const priceRegularPriceItemRegular = priceRegular?.querySelector('.price-item--regular');
        const priceSalePriceItemRegular = priceSale?.querySelector('.price-item--regular');
        const priceSalePriceItemSale = priceSale?.querySelector('.price-item--sale');
        
        if (priceRegularPriceItemRegular) priceRegularPriceItemRegular.textContent = formatPrice(this.currentVariant.price);
        if (priceSalePriceItemSale) priceSalePriceItemSale.textContent = formatPrice(this.currentVariant.price);

        if (priceSalePriceItemRegular) {
          if (this.currentVariant.compare_at_price > this.currentVariant.price) {
            priceSalePriceItemRegular.textContent = formatPrice(this.currentVariant.compare_at_price);
            price.classList.add('price--on-sale');
          } else {
            priceSalePriceItemRegular.textContent = '';
            price.classList.remove('price--on-sale');
          }
        }
      }

      updateCurrentVariantId() {
        const inputIdElements = this.card?.querySelectorAll('.product-variant-id');
        inputIdElements.forEach((element) => {
          element.value = this.currentVariant.id;
        });
      }

      updateButtonStatuses() {
        const productForm = this.card?.querySelector('product-form');
        productForm?.toggleSubmitButton(!this.currentVariant.available, window.variantStrings.soldOut);
      }

      dispatchVariantChangeEvent() {
        this.card.dispatchEvent(
          new CustomEvent('card-product-variant:change', {
            bubbles: true,
            detail: {
              variant: this.currentVariant,
              oldVariant: this.oldVariant
            },
          })
        );
      }
    }
  );
}